// Pure throttle for the outbound experience.position frame (R-DISP-06 /
// R-SAFE-01): at most every 5 seconds, when the player has moved
// 10 metres or accuracy has materially improved since the last send. No DOM, no timers of its own, so
// the decision can be unit-tested and driven by whatever clock the caller has.
import { distanceM } from '../map/geo';
import type { LatLng } from '../map/geo';

export const POSITION_THROTTLE_MS = 5000;
export const POSITION_THROTTLE_MIN_MOVE_M = 10;

export interface PositionThrottleState {
  lastSentAtMs: number | null;
  lastSentPos: (LatLng & { accuracyM?: number }) | null;
}

export const INITIAL_THROTTLE_STATE: PositionThrottleState = {
  lastSentAtMs: null,
  lastSentPos: null,
};

export interface ThrottleDecision {
  send: boolean;
  nextState: PositionThrottleState;
}

/**
 * Decides whether `next` should go out now, given the state of the last send
 * and the current time. The first fix always sends (nothing to throttle
 * against yet). After that, the elapsed-time gate and either movement or materially improved
 * accuracy must pass (at least 5 metres and 25 percent better).
 */
export function shouldSendPosition(
  state: PositionThrottleState,
  next: LatLng & { accuracyM?: number },
  nowMs: number,
  /**
   * The player is inside the destination's arrival radius. A player standing
   * still there must keep sending fresh fixes (the server only accepts recent
   * ones), so the movement/accuracy gate is skipped; the time gate still holds.
   */
  insideArrival = false
): ThrottleDecision {
  if (state.lastSentAtMs === null || state.lastSentPos === null) {
    return { send: true, nextState: { lastSentAtMs: nowMs, lastSentPos: next } };
  }
  const elapsedMs = nowMs - state.lastSentAtMs;
  if (elapsedMs < POSITION_THROTTLE_MS) {
    return { send: false, nextState: state };
  }
  const movedM = distanceM(state.lastSentPos, next);
  const previousAccuracy = state.lastSentPos.accuracyM;
  const improvedAccuracy = Number.isFinite(next.accuracyM) && Number.isFinite(previousAccuracy)
    && next.accuracyM! > 0 && previousAccuracy! - next.accuracyM! >= 5
    && next.accuracyM! <= previousAccuracy! * 0.75;
  if (!insideArrival && movedM < POSITION_THROTTLE_MIN_MOVE_M && !improvedAccuracy) {
    return { send: false, nextState: state };
  }
  return { send: true, nextState: { lastSentAtMs: nowMs, lastSentPos: next } };
}
